import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuota } from '../../components/quota/useQuota';
import { ApiError } from '../../lib/apiClient';
import { getErrorMessage } from '../../lib/errors';
import type { RejectionReason } from '../../types/api';
import { rejectRequest } from '../generation/generationApi';
import { rememberReplacement } from './rejectionTrail';
import { MAX_REGENERATIONS } from './reviewLimits';

export interface RejectionFlow {
  isSubmitting: boolean;
  error: string | null;
  /** Regeneration attempts this lineage has already spent. */
  used: number;
  /**
   * The lineage has spent its three regeneration attempts. Regenerating is
   * closed from here on — a fact about the row, so dismissing the panel below
   * does not reopen it.
   */
  isCapped: boolean;
  /**
   * Whether the cap panel is on screen: capped, and not yet dismissed. The
   * dismissal is the user having taken one of the two paths it offers.
   */
  showCapPanel: boolean;
  /** Sends the rejection and navigates to the request the regeneration runs under. */
  submit: (feedback: string, reason: RejectionReason) => void;
  /** Clears the last message — the reviewer changed what they are asking for. */
  reset: () => void;
  /** Dismisses the cap panel — the user has picked one of the two paths. */
  dismissCap: () => void;
}

/** Message for the domain errors a rejection can provoke. */
function rejectionMessage(caught: unknown): string {
  if (caught instanceof ApiError) {
    switch (caught.code) {
      case 'REQUEST_NOT_AWAITING_REVIEW':
        return 'This post has already moved past review. Reload it to see where it stands.';
      case 'QUOTA_EXCEEDED':
        return 'Rejecting starts a new post, and you have no posts left this month. Accept the current draft or edit its layers instead.';
      default:
        return caught.message;
    }
  }
  return getErrorMessage(caught, 'That did not send. Try again.');
}

/**
 * The rejection half of both review steps.
 *
 * A rejection is not an edit of this request: the backend opens a **new** one
 * for the regeneration and returns it, so the only sane thing to do is follow it
 * to `/review/{new_id}`. The brief itself is untouched.
 *
 * The cap is the SRS invariant this hook exists to hold: three regeneration
 * attempts per lineage, then `REJECTION_CAP_REACHED` and the user chooses
 * between exactly two paths. There is deliberately no retry affordance for a
 * capped lineage — the way to start over is a brand-new request, which resets
 * the counter.
 *
 * The cap is read off the **row** (`attempt`), not off a 409 this session
 * happened to see, so a reload lands on the same screen the reviewer left; the
 * refusal is still honoured as a fallback, since the server is the enforcer.
 * Dismissing is a UI act — the panel closes, the row of course does not move —
 * and it is what "accept current best" does.
 *
 * A 429 `QUOTA_EXCEEDED` is deliberately handled the other way round: it is not
 * a state change in this lineage, so the dialog stays open carrying the message
 * and the user still has approve and layer-edit in front of them.
 */
export function useRejectRequest(
  businessId: string,
  requestId: string,
  /** `request.attempt` — position in the lineage, starting at 1. */
  attempt: number,
): RejectionFlow {
  const navigate = useNavigate();
  const { refresh: refreshQuota } = useQuota();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isRefused, setIsRefused] = useState(false);
  const [isCapDismissed, setIsCapDismissed] = useState(false);

  const reset = useCallback(() => setError(null), []);

  const dismissCap = useCallback(() => setIsCapDismissed(true), []);

  const submit = useCallback(
    (feedback: string, reason: RejectionReason) => {
      if (isSubmitting) return;

      setError(null);
      setIsSubmitting(true);

      void (async () => {
        try {
          const replacement = await rejectRequest(businessId, requestId, { feedback, reason });
          rememberReplacement(requestId, replacement.id);
          setIsSubmitting(false);
          // A rejection enqueues the next attempt, which is charged like any
          // other run — the header counter has to move with it.
          refreshQuota();
          void navigate(`/review/${replacement.id}`);
        } catch (caught) {
          setIsSubmitting(false);
          if (caught instanceof ApiError && caught.code === 'REJECTION_CAP_REACHED') {
            // Not an error the user can act on by retrying — it is a state
            // change. The two remaining paths take over.
            setIsRefused(true);
            setIsCapDismissed(false);
            return;
          }
          setError(rejectionMessage(caught));
        }
      })();
    },
    [businessId, isSubmitting, navigate, refreshQuota, requestId],
  );

  const isCapped = isRefused || attempt >= MAX_REGENERATIONS;

  return {
    isSubmitting,
    error,
    // Attempts on the lineage so far, the row's own count (see ReviewBudget).
    used: Math.min(Math.max(attempt, 1), MAX_REGENERATIONS),
    isCapped,
    showCapPanel: isCapped && !isCapDismissed,
    submit,
    reset,
    dismissCap,
  };
}
